import DeleteIcon from '@mui/icons-material/Delete';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { dayPartApi, dayPartKeys } from '@/api/account-management/dayPartApi';
import { type Location } from '@/models/account-management/Location';
import { type DayPart, type DayPartOverrideInput } from '@/models/account-management/DayPart';
import { minuteToTime, timeToMinute } from '@/models/media-network/PlaylistSchedule';
import { useLocalizedStrings } from '../../../localization/LocalizedStringsProvider';
import { useToast } from '../../common/toast/ToastProvider';
import ReadOnlyFieldset from '../../common/dialog/ReadOnlyFieldset';
import { useAssignedLocations } from '../../../permissions/useLocationScope';

interface Props {
    open: boolean;
    brandId: string;
    dayPart: DayPart | null; // null = create
    // View-only: the grid opens this with an eye rather than a pencil for a user with read access alone.
    readOnly?: boolean;
    onClose: () => void;
}

interface OverrideRow {
    locationId: string;
    startTime: string;
    endTime: string;
}

const DayPartDialog = ({ open, brandId, dayPart, readOnly, onClose }: Props) => {
    const qc = useQueryClient();
    const toast = useToast();
    const strings = useLocalizedStrings();

    const [name, setName] = useState('');
    const [startTime, setStartTime] = useState('06:00');
    const [endTime, setEndTime] = useState('11:00');
    const [overrides, setOverrides] = useState<OverrideRow[]>([]);
    const [newLocationId, setNewLocationId] = useState('');

    // Only the locations this user is assigned to - the per-location override picker offered them all (TTD-160).
    const { locations } = useAssignedLocations(brandId, { enabled: open });

    useEffect(() => {
        if (!open) return;
        setName(dayPart?.name ?? '');
        setStartTime(minuteToTime(dayPart?.startMinute) || '06:00');
        setEndTime(minuteToTime(dayPart?.endMinute) || '11:00');
        setOverrides(dayPart?.locationOverrides.map(o => ({
            locationId: o.locationId,
            startTime: minuteToTime(o.startMinute),
            endTime: minuteToTime(o.endMinute),
        })) ?? []);
        setNewLocationId('');
    }, [open, dayPart]);

    const { mutateAsync: save, isPending } = useMutation({
        mutationFn: async () => {
            const body = {
                name: name.trim(),
                startMinute: timeToMinute(startTime)!,
                endMinute: timeToMinute(endTime)!,
            };

            const id = dayPart
                ? (await dayPartApi.update(dayPart.id, body), dayPart.id)
                : (await dayPartApi.create({ ...body, brandId })).id;

            // Overrides are replaced wholesale — an empty list clears them all, which is how a row is removed.
            const payload: DayPartOverrideInput[] = overrides
                .filter(o => timeToMinute(o.startTime) !== null && timeToMinute(o.endTime) !== null)
                .map(o => ({
                    locationId: o.locationId,
                    startMinute: timeToMinute(o.startTime)!,
                    endMinute: timeToMinute(o.endTime)!,
                }));

            await dayPartApi.setOverrides(id, payload);
        },
        onSuccess: async () => {
            toast({ severity: 'success', message: strings.dayPartSaved });
            await qc.invalidateQueries({ queryKey: dayPartKeys.list(brandId) });
            // Schedules render their window from the day part, so moving one changes what they display.
            await qc.invalidateQueries({ queryKey: ['promotion-schedules'] });
            onClose();
        },
        onError: () => toast({ severity: 'error', message: strings.dayPartSaveError }),
    });

    const addOverride = () => {
        if (!newLocationId || overrides.some(o => o.locationId === newLocationId)) return;
        setOverrides([...overrides, { locationId: newLocationId, startTime, endTime }]);
        setNewLocationId('');
    };

    const locationName = (id: string) => locations?.find(l => l.id === id)?.name ?? id;

    // Equal times are ambiguous — zero-length or all-day? — and rejected by the backend.
    const timesValid = startTime !== endTime;
    const overridesValid = overrides.every(o => o.startTime !== o.endTime);
    const canSave = !!name.trim() && timesValid && overridesValid;
    const wraps = startTime > endTime;

    const available = (locations ?? []).filter(l => !overrides.some(o => o.locationId === l.id));

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{dayPart ? strings.dayPartEdit : strings.dayPartNew}</DialogTitle>
            <DialogContent>
                <ReadOnlyFieldset readOnly={readOnly}>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        label={strings.name}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        helperText={strings.dayPartNameHelp}
                        fullWidth
                        autoFocus
                    />

                    <Stack direction="row" spacing={2}>
                        <TextField
                            label={strings.dayPartStart}
                            type="time"
                            value={startTime}
                            onChange={(e) => setStartTime(e.target.value)}
                            slotProps={{ inputLabel: { shrink: true } }}
                            fullWidth
                        />
                        <TextField
                            label={strings.dayPartEnd}
                            type="time"
                            value={endTime}
                            onChange={(e) => setEndTime(e.target.value)}
                            error={!timesValid}
                            helperText={
                                !timesValid ? strings.dayPartTimesMustDiffer
                                : wraps ? strings.dayPartWrapsMidnight
                                : ' '
                            }
                            slotProps={{ inputLabel: { shrink: true } }}
                            fullWidth
                        />
                    </Stack>

                    <Divider />

                    <Typography variant="subtitle2">{strings.dayPartOverrides}</Typography>
                    {/* The reason day parts are centrally defined: one store can serve breakfast until noon
                        without "Breakfast" meaning something different everywhere else. */}
                    <Typography variant="caption" color="text.secondary">{strings.dayPartOverridesHelp}</Typography>

                    {overrides.map((o, index) => (
                        <Stack key={o.locationId} direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>{locationName(o.locationId)}</Typography>
                            <TextField
                                type="time"
                                value={o.startTime}
                                onChange={(e) => setOverrides(overrides.map((r, i) => i === index ? { ...r, startTime: e.target.value } : r))}
                                size="small"
                                sx={{ width: 130 }}
                            />
                            <TextField
                                type="time"
                                value={o.endTime}
                                onChange={(e) => setOverrides(overrides.map((r, i) => i === index ? { ...r, endTime: e.target.value } : r))}
                                error={o.startTime === o.endTime}
                                size="small"
                                sx={{ width: 130 }}
                            />
                            <IconButton size="small" color="error" onClick={() => setOverrides(overrides.filter((_, i) => i !== index))} aria-label="remove override">
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    ))}

                    <Stack direction="row" spacing={1} alignItems="center">
                        <FormControl size="small" sx={{ flex: 1 }}>
                            <InputLabel id="override-location-label">{strings.location}</InputLabel>
                            <Select
                                labelId="override-location-label"
                                label={strings.location}
                                value={newLocationId}
                                onChange={(e) => setNewLocationId(e.target.value)}
                            >
                                {available.map(l => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <Button onClick={addOverride} disabled={!newLocationId}>{strings.add}</Button>
                    </Stack>
                </Stack>
                </ReadOnlyFieldset>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={onClose}>{readOnly ? strings.close : strings.cancel}</Button>
                {!readOnly &&
                <Button variant="contained" loading={isPending} disabled={!canSave || isPending} onClick={() => save()}>
                    {strings.save}
                </Button>
                }
            </DialogActions>
        </Dialog>
    );
};

export default DayPartDialog;
